import { q } from './workspace/quote.js';
import { normalize } from './posix.js';
import { isEditableText, validateRelPath } from './files.js';
import { MAX_FILES, MAX_FILE_CHARS, MAX_FILES_TOTAL, capMentorFiles } from './mentor-context.js';
import { inTurn } from './session.js';
/**
 * The files a mentor is shown: what a person looking over the learner's
 * shoulder would open.
 *
 * In this order, because the order is the priority when the caps bite:
 *
 *   1. **conflicted** — git stopped and wrote a question into these files, so
 *      their content, markers and all, is the state of the level.
 *   2. **checked** — named by a failing, visible objective: its `with.path`,
 *      a nested check's, or a file a `shell` check's script runs (l09's
 *      `tests` objective is six `node -e "import('./src/cart/pricing.js')…"`
 *      and names no `path` at all, and pricing.js is the file the whole
 *      incident was about) — and named in the level's visible text as well
 *      (`checkedPaths` says why).
 *   3. **changed** — tracked and different from HEAD, staged or not; then
 *      what has been committed since the level started (`startHead`,
 *      session.ts), which a clean tree would otherwise hide entirely.
 *   4. **created** — untracked, so the learner wrote it.
 *
 * Each file's diff is against HEAD, or against where the level started once
 * HEAD has moved from there, so that one entry carries committed and
 * uncommitted work together. For a file no commit since the start has
 * touched, the two are the same diff.
 *
 * ## One round trip
 *
 * In the browser every command is a serial round trip into the emulator, and
 * those were measured at hundreds of milliseconds each — a level open went
 * from 6–20 s to 2–5 s by cutting them. So this is one `bash` script whatever
 * the tree looks like: it asks git for the lists itself, walks the
 * candidates in priority order, skips what is not wanted, and prints each
 * kept file's content and diff between delimiters carrying a random token, so
 * nothing a learner writes into a file can pose as the next section. It stops
 * after `MAX_FILES` files or `MAX_FILES_TOTAL` bytes, and it reads at most
 * `SCAN_NAMES` names from each of git's lists, with dependency trees pruned
 * before any are counted, so a tree with nine thousand untracked files costs
 * a bounded amount to describe rather than one proportional to its mess. No
 * command at all is run when there is nothing to look for.
 *
 * It runs in the workspace's turn (session.ts, `inTurn`), never beside a
 * grade, and its git is run the way a look the learner did not ask for should
 * run it: against a copy of the index, so it cannot be why their `git add` is
 * refused, and reading every pathspec literally, so a file named `[ab].js`
 * is not a glob that answers with `a.js` and `b.js`.
 *
 * ## What is refused
 *
 * Anything outside the level's working directory: a named path goes through
 * `validateRelPath` before it is sent, git's lists are asked for `--relative`,
 * and a path with a symbolic link anywhere along it is skipped in the guest,
 * because a link is the one way a clean relative path can still lead out.
 * `.git` and `node_modules` are skipped wherever they appear, and so is
 * anything binary: `grep -I` in the guest, and `isEditableText` here for what
 * that lets through. A failing check's file is sent only when the level has
 * already named it to the learner, and never when the seed finishes its name
 * (`checkedPaths`).
 */
/** Paths the failing objectives may name, at most. Past this they are the same few files again. */
const MAX_CHECKED = 24;
/** Bytes of one diff read in the guest before it is cut to its cap, so a vast diff is never held whole. */
const DIFF_READ_BYTES = 64 * 1024;
/**
 * Names read from each of git's lists, at most, once dependency trees are
 * pruned. Each one kept can cost a `grep` to see whether it is text, which is
 * a process in an emulator, and the whole script has a 20 s budget.
 */
const SCAN_NAMES = 400;
const WHY = new Set(['checked', 'conflicted', 'changed', 'created']);
/** A full object name, SHA-1 or SHA-256. Anything else is not written into the script. */
const OBJECT_NAME = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/i;
/** The level's starting commit as the script may use it, or null when there is none to trust. */
function startCommit(raw) {
    return typeof raw === 'string' && OBJECT_NAME.test(raw) ? raw.toLowerCase() : null;
}
/**
 * A path inside a `shell` check's script: `./src/cart/pricing.js`,
 * `test/pricing.test.js`. A token counts only if it starts with `./` or has a
 * directory in it, and ends in an extension, which keeps `process.exit` and
 * `m.applyCoupon` out; `/dev/null` is absolute and never matches. A false
 * positive costs nothing — a path that is not a file is skipped in the guest.
 */
const SCRIPT_PATH = /(?:^|[\s'"`(=<>|;,])((?:\.\/)?(?:[\w@.-]+\/)*[\w@-][\w@.-]*\.[A-Za-z0-9]{1,10})(?=$|[\s'"`),;|<>])/g;
function pathsInScript(run) {
    const found = [];
    for (const m of run.matchAll(SCRIPT_PATH)) {
        const token = m[1];
        if (token.startsWith('./') || token.includes('/'))
            found.push(token);
    }
    return found;
}
/**
 * Every file one check spec names, nested checks included.
 *
 * `path` means a file everywhere except the HTTP checks, where `http_json`
 * uses it for a place inside a JSON document (`orders.0.sku`).
 */
function pathsIn(spec, out) {
    if (!spec || typeof spec !== 'object')
        return;
    const { check, with: params } = spec;
    if (!params || typeof params !== 'object')
        return;
    const p = params;
    const kind = typeof check === 'string' ? check : '';
    if (!kind.startsWith('http_') && typeof p.path === 'string')
        out.push(p.path);
    if (kind === 'shell' && typeof p.run === 'string')
        out.push(...pathsInScript(p.run));
    if (Array.isArray(p.checks))
        for (const sub of p.checks)
            pathsIn(sub, out);
}
/** A path as the level's working directory knows it, or null if it is not one this may read. */
function relPath(raw) {
    try {
        const rel = validateRelPath(normalize(raw.replace(/^(\.\/)+/, '')));
        if (!rel || rel.split('/').includes('node_modules'))
            return null;
        return rel;
    }
    catch {
        return null;
    }
}
/** The `detail` sentences in a check's parameters, nested checks' included: each is shown when its check fails. */
function detailsIn(params, out) {
    if (!params || typeof params !== 'object')
        return;
    const p = params;
    if (typeof p.detail === 'string')
        out.push(p.detail);
    if (!Array.isArray(p.checks))
        return;
    for (const sub of p.checks) {
        if (sub && typeof sub === 'object')
            detailsIn(sub.with, out);
    }
}
/** A word of prose that could be a path: letters, digits and `_@.-/`. Backticks, punctuation and Arabic all end one. */
const PROSE_WORD = /[\w@./-]+/g;
/**
 * Whether the level itself names a path to the learner, in the text they are
 * shown before they have solved anything: the brief, the constraints, and
 * each visible objective's title, `fail`, `detail` sentences and diagnostic
 * messages. Not the hints, which are paid for, and not the reflection, which
 * is shown only after a pass — the same list as scripts/mentor-secrets.mjs's
 * `visibleText`, without the reflection.
 *
 * Text as it stands on the mission, so a level switched to Arabic is asked
 * about its Arabic, where paths stay in backticks as they are. A path counts
 * as written relative to the working directory or to the box above it, since
 * levels whose `cwd` is `ops` say `ops/run/digest.job` for a check's
 * `run/digest.job`.
 */
function namedByLevel(mission) {
    const text = [mission.brief ?? '', ...(mission.constraints ?? [])];
    for (const o of mission.objectives) {
        if (o.hidden === true)
            continue;
        text.push(o.title ?? '', o.fail ?? '');
        detailsIn(o.with, text);
        for (const d of o.diagnostics ?? []) {
            text.push(d.message ?? '');
            detailsIn(d.with, text);
        }
    }
    const words = new Set();
    for (const m of text.join('\n').matchAll(PROSE_WORD)) {
        // `./src/a.js` is `src/a.js`, and a sentence that ends on a path ends in
        // a full stop that is not part of it.
        const word = m[0].replace(/^(\.\/)+/, '').replace(/\.+$/, '');
        if (word)
            words.add(word);
    }
    const cwd = relPath(mission.cwd ?? '');
    return (rel) => words.has(rel) || (!!cwd && words.has(`${cwd}/${rel}`));
}
/**
 * The files named by failing, visible objectives, in author order.
 *
 * Only objectives that failed. Not one that passed, which is evidence of
 * nothing; not a hidden one, whose check is the answer key; and not one that
 * was skipped because what it requires has not passed yet — it was never
 * run, and its check is often the step after the one the learner is on. l52's
 * `found-pid` runs `bash tools/sale-prices.sh` to find a process, and that
 * name is what the level is asking the learner to discover.
 *
 * Read from the checks as the author wrote them, not rendered, and any path
 * with a `{{…}}` in it is dropped. A path the seed has to finish is a
 * per-learner value, and on a level like l54 it is the answer itself:
 * `spool/{{seed.PENDING}}.json` is the one order nobody has recorded, and
 * finding which one that is is the task. The Worker's filter has no way to
 * hold back a value it was never told was secret. (A script path the seed
 * finishes cannot be read out of the unrendered script anyway: `{` ends a
 * path in `SCRIPT_PATH`.)
 *
 * And only a path the level's visible text names too (`namedByLevel`). A
 * check's script is an author's instructions to the grader, and a file it
 * happens to touch is not thereby a file the learner has been told about;
 * the brief is. l09's pricing.js is in its brief, so it still comes through.
 * What the learner has changed, created or left conflicted comes through git
 * whatever the level says.
 */
export function checkedPaths(mission, grade) {
    if (!grade)
        return [];
    const results = new Map(grade.objectives.map((o) => [o.id, o]));
    const named = namedByLevel(mission);
    const out = [];
    for (const o of mission.objectives) {
        const r = results.get(o.id);
        if (!r || r.passed || r.skipped || r.hidden || o.hidden === true)
            continue;
        const raw = [];
        pathsIn({ check: o.check, with: o.with }, raw);
        for (const p of raw) {
            if (p.includes('{{'))
                continue;
            const rel = relPath(p);
            if (rel && named(rel) && !out.includes(rel))
                out.push(rel);
        }
        if (out.length >= MAX_CHECKED)
            break;
    }
    return out.slice(0, MAX_CHECKED);
}
/** A delimiter nothing in a file can contain by accident or on purpose: it is chosen after the file was written. */
function newToken() {
    let hex = '';
    while (hex.length < 20)
        hex += Math.floor(Math.random() * 16).toString(16);
    return `EOS${hex}`;
}
/**
 * The one script.
 *
 * `LC_ALL=C` so that `${#c}` and `${c:0:n}` count bytes, as `head -c` does:
 * the budget is spent in bytes in the guest, which is never more characters
 * than the caps allow, and the exact character caps are applied again here.
 * Content goes through a variable (`$(…; printf x)` keeps its trailing
 * newlines) so its length can be taken without a second read; `grep -I` has
 * already turned away anything with a NUL in it, which is all a variable
 * cannot hold.
 *
 * `GIT_LITERAL_PATHSPECS=1` for every git in it, because `git diff HEAD --
 * "$p"` reads `$p` as a pattern: a file named `*.js`, `[ab].js` or `:(top)x`
 * would be sent under its own name with other files' diffs, or the whole
 * repository's. And `GIT_INDEX_FILE` at a copy of the index: `git diff`
 * rewrites the index when it finds a file whose stat changed and whose
 * content did not, under `index.lock`, and `GIT_OPTIONAL_LOCKS=0` does not
 * stop it — that governs `git status` (measured on git 2.46). With the copy,
 * the rewrite and its lock are the copy's, in /tmp. When no copy can be made
 * the real index is used as before.
 *
 * `start` is the level's starting commit, already checked to be a full
 * object name (`startCommit`), or null. `base` is what every diff is taken
 * against: that commit when it exists and HEAD has moved from it, HEAD
 * otherwise.
 *
 * Output, per file: `\n<T> F <why> <path>`, then `\n<T> C <cut>` and the
 * content, then `\n<T> D <cut>` and the diff — either section absent when
 * there is none — and `\n<T> Z` once at the end.
 */
function evidenceScript(token, checked, git, start) {
    return [
        'set -f',
        'export LC_ALL=C GIT_LITERAL_PATHSPECS=1',
        `T=${token}`,
        `S=${start ?? ''}`,
        `max=${MAX_FILES} cap=${MAX_FILE_CHARS} left=${MAX_FILES_TOTAL} count=0 git=${git ? 1 : 0} head=0 hd= base=HEAD`,
        "nl=$'\\n'",
        'seen=$nl',
        'if [ "$git" = 1 ] && idx=$(git rev-parse --git-path index 2>/dev/null) && ix=$(mktemp 2>/dev/null); then',
        `  trap 'rm -f "$ix" "$ix.lock"' EXIT`,
        '  cp -- "$idx" "$ix" 2>/dev/null && export GIT_INDEX_FILE=$ix',
        'fi',
        '[ "$git" = 1 ] && hd=$(git rev-parse -q --verify HEAD 2>/dev/null) && head=1',
        // A start that is HEAD adds nothing, and one this repository does not
        // have (the learner deleted .git and ran `git init` again) is ignored
        // rather than trusted.
        'if [ "$head" = 1 ] && [ -n "$S" ] && [ "$S" != "$hd" ] && git cat-file -e "$S^{commit}" 2>/dev/null; then base=$S; fi',
        'emit() {',
        '  local why=$1 p=$2 rest=$2 part acc= c= d= ct=0 dt=0 have=0 want',
        '  [ "$count" -lt "$max" ] && [ "$left" -gt 0 ] || return 0',
        '  case "$p" in "" | *"$nl"*) return 0 ;; esac',
        // The refusals that cost nothing come first. `seen` holds only the names
        // printed, so it never grows past `max` and finding a name in it stays
        // cheap however many were turned away; a name turned away in one list and
        // offered again by the next is turned away again for the same reason.
        '  case "/$p/" in */.git/* | */node_modules/*) return 0 ;; esac',
        '  case "$seen" in *"$nl$p$nl"*) return 0 ;; esac',
        // Every component, and none of them may be git's, a dependency tree, a
        // step outside, or a link: a link is the one way a clean relative path
        // leads somewhere else.
        '  while :; do',
        '    case "$rest" in */*) part=${rest%%/*}; rest=${rest#*/} ;; *) part=$rest; rest= ;; esac',
        '    case "$part" in "" | . | .. | .git | node_modules) return 0 ;; esac',
        '    acc=${acc:+$acc/}$part',
        '    [ -L "$acc" ] && return 0',
        '    [ -n "$rest" ] || break',
        '  done',
        '  if [ -e "$p" ]; then',
        '    [ -f "$p" ] || return 0',
        '    if [ -s "$p" ] && ! grep -qI "" -- "$p" 2>/dev/null; then return 0; fi',
        '    want=$(( left < cap ? left : cap ))',
        '    c=$(head -c $((want + 1)) -- "$p" 2>/dev/null; printf x); c=${c%x}',
        '    if [ "${#c}" -gt "$want" ]; then c=${c:0:$want}; ct=1; fi',
        '    left=$((left - ${#c}))',
        '    have=1',
        '  fi',
        // A conflicted file's diff against HEAD is its content again with plus
        // signs on it; the markers already say which side is which.
        '  if [ "$head" = 1 ] && [ "$why" != conflicted ]; then',
        '    if [ "$left" -gt 0 ]; then',
        `      d=$(git diff "$base" -- "$p" 2>/dev/null | head -c ${DIFF_READ_BYTES}; printf x); d=\${d%x}`,
        '      want=$(( left < cap ? left : cap ))',
        '      if [ "${#d}" -gt "$want" ]; then d=${d:0:$want}; dt=1; fi',
        '      left=$((left - ${#d}))',
        '    elif [ "$why" = changed ]; then',
        '      dt=1',
        '    fi',
        '  fi',
        '  [ "$have" = 1 ] || [ -n "$d" ] || return 0',
        '  seen=$seen$p$nl',
        '  count=$((count + 1))',
        '  printf "\\n%s F %s %s\\n" "$T" "$why" "$p"',
        '  if [ "$have" = 1 ]; then printf "\\n%s C %s\\n" "$T" "$ct"; printf "%s" "$c"; fi',
        '  if [ -n "$d" ] || [ "$dt" = 1 ]; then printf "\\n%s D %s\\n" "$T" "$dt"; printf "%s" "$d"; fi',
        '}',
        'full() { [ "$count" -ge "$max" ] || [ "$left" -le 0 ]; }',
        // Git's lists, one name to a line. `-z` so that no name comes back
        // C-quoted, then NUL to newline, so that the one name this cannot carry —
        // one with a newline in it — breaks into lines that are not files and are
        // skipped. Read from a here-string rather than `< <(…)`: process
        // substitution needs /dev/fd, which the guest's devtmpfs does not make.
        //
        // Pruned and cut before the shell sees a line: a dependency tree nobody
        // ignored is tens of thousands of names that would each be read, matched
        // and refused one at a time, and past `SCAN_NAMES` the list is noise in
        // any case. The untracked walk is told not to go into one at all.
        `names() { git "$@" 2>/dev/null | tr "\\0" "\\n" | grep -v -e '^node_modules/' -e '/node_modules/' -e '^\\.git/' -e '/\\.git/' | head -n ${SCAN_NAMES}; }`,
        'if [ "$git" = 1 ]; then',
        '  list=$(names diff --name-only -z --relative --diff-filter=U)',
        '  while IFS= read -r p; do emit conflicted "$p"; full && break; done <<< "$list"',
        'fi',
        `for p in ${checked.map(q).join(' ')}; do emit checked "$p"; full && break; done`,
        'if [ "$git" = 1 ]; then',
        '  if [ "$head" = 1 ]; then list=$(names diff --name-only -z --relative HEAD)',
        '  else list=$(names diff --name-only -z --relative --cached); fi',
        '  while IFS= read -r p; do full && break; emit changed "$p"; done <<< "$list"',
        // What was committed since the level started: in a tree the learner has
        // committed, the only place their work still shows. A file already sent
        // above is not sent twice, and its diff was against the start already.
        //
        // `changed`, because the contract's `why` is a closed union the Worker
        // checks. That also means the Worker's filter lets a reply quote whole
        // lines of these files (worker/mentor-filter.ts, `learnerEvidence`), and
        // that is right here too: a committed file is the learner's own work, on
        // their disk, and pressing Check grades it, so quoting a line of it back
        // confirms nothing Check would not. What a merge or rebase brought in
        // from other branches is in the same position as a seeded file that a
        // `checked` entry already carries.
        '  if [ "$base" != HEAD ]; then',
        '    list=$(names diff --name-only -z --relative "$base" HEAD)',
        '    while IFS= read -r p; do full && break; emit changed "$p"; done <<< "$list"',
        '  fi',
        '  list=$(names ls-files -z --others --exclude-standard --exclude=node_modules/)',
        '  while IFS= read -r p; do full && break; emit created "$p"; done <<< "$list"',
        'fi',
        'printf "\\n%s Z\\n" "$T"',
    ].join('\n');
}
/** A cut read can end in the first bytes of a character, which decode as U+FFFD; they are not the learner's. */
function trimCut(text, cut) {
    return cut ? text.replace(/�+$/, '') : text;
}
/** The script's output, back into files. Anything unrecognisable is dropped rather than guessed at. */
function parseEvidence(stdout, token) {
    const parts = stdout.split(new RegExp(`\\n${token} ([FCDZ])([^\\n]*)\\n`));
    const files = [];
    let file = null;
    for (let i = 1; i < parts.length; i += 3) {
        const kind = parts[i];
        const args = parts[i + 1] ?? '';
        const body = parts[i + 2] ?? '';
        if (kind === 'F') {
            const rest = args.slice(1);
            const space = rest.indexOf(' ');
            const why = space > 0 ? rest.slice(0, space) : '';
            const path = space > 0 ? rest.slice(space + 1) : '';
            file = null;
            if (!WHY.has(why) || relPath(path) !== path)
                continue;
            file = { path, why: why };
            files.push(file);
        }
        else if (kind === 'C' && file) {
            const cut = args.trim() === '1';
            const content = trimCut(body, cut);
            // Binary that `grep -I` let through, or bytes that are not UTF-8: the
            // name and the diff are still worth having, the text is not.
            if (isEditableText(content))
                file.content = content;
            if (cut)
                file.truncated = true;
        }
        else if (kind === 'D' && file) {
            const cut = args.trim() === '1';
            const diff = trimCut(body, cut);
            if (diff.length > 0)
                file.diff = diff;
            if (cut)
                file.truncated = true;
        }
        else if (kind === 'Z') {
            break;
        }
    }
    return files;
}
/**
 * The evidence for one question to the mentor: at most `MAX_FILES` files,
 * within the per-file and total caps, in priority order.
 *
 * `state` decides whether git is asked anything: not in a directory that is
 * not a repository, and not in a clean tree whose HEAD is still where the
 * level started, which has nothing conflicted, changed, committed or created
 * and no diff to show. `grade` decides which files are `checked`; with none,
 * no objective is known to be failing and none is. `_seed` is unused and
 * kept for the callers that pass it: the checks are read as written
 * (`checkedPaths`), because a path the seed finishes is one this does not
 * send.
 *
 * `startHead` is the session's (session.ts), and optional for the callers
 * that have no session: without it, or with anything that is not a full
 * object name, nothing committed is looked for and every diff is against
 * HEAD, as before it existed. Without it, a learner who commits their work
 * leaves the mentor looking at a clean tree, and on a level whose failing
 * check is hidden or names no file, at nothing at all.
 *
 * Takes the workspace's turn (session.ts, `inTurn`), so it never runs beside
 * a Check or a look. Never throws. A workspace that cannot answer is a
 * question with less evidence in it, which is a worse answer and not a
 * broken one.
 */
export async function gatherMentorEvidence(mission, ws, state, grade, _seed, startHead) {
    const checked = checkedPaths(mission, grade);
    const start = startCommit(startHead);
    const moved = start !== null && state.head.hash !== null && state.head.hash !== start;
    const git = state.isRepo && (!state.status.clean || moved);
    if (checked.length === 0 && !git)
        return [];
    const token = newToken();
    try {
        const r = await inTurn(ws, () => ws.exec(evidenceScript(token, checked, git, start), { cwd: mission.cwd, timeoutMs: 20_000 }));
        return capMentorFiles(parseEvidence(r.stdout, token));
    }
    catch {
        return [];
    }
}
//# sourceMappingURL=mentor-evidence.js.map